import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Stack,
  Paper,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DailyCountRow {
  date: string; // YYYY-MM-DD
  journey_count: number;
  unique_vehicle_count: number;
}

interface DailyCountsResponse {
  data: DailyCountRow[];
  count: number;
  unique_vehicles_in_range: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function todayStr(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function firstOfMonthStr(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}-01`;
}

const API_BASE = '/api/nmac-journeys/daily-counts';
const MAX_RANGE_DAYS = 92; // ~3 months — matches server-side validation

function diffDays(fromStr: string, toStr: string): number {
  const from = new Date(fromStr + 'T00:00:00');
  const to = new Date(toStr + 'T00:00:00');
  return Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
}

async function fetchDailyCounts(date_from: string, date_to: string): Promise<DailyCountsResponse> {
  const url = `${API_BASE}?date_from=${encodeURIComponent(date_from)}&date_to=${encodeURIComponent(date_to)}`;
  const res = await fetch(url);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `API error ${res.status}`);
  }
  return res.json();
}

// ─── Component ────────────────────────────────────────────────────────────────

const NmacJourneysPage: React.FC = () => {
  const [dateFrom, setDateFrom] = useState<string>(firstOfMonthStr());
  const [dateTo, setDateTo] = useState<string>(todayStr());
  const [data, setData] = useState<DailyCountRow[]>([]);
  const [totals, setTotals] = useState<{
    journeys: number;
    uniqueVehicles: number;
    activeDays: number;
  }>({
    journeys: 0,
    uniqueVehicles: 0,
    activeDays: 0,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetched = useRef(false);

  const load = useCallback(async (from: string, to: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchDailyCounts(from, to);
      const rows = result.data || [];
      setData(rows);

      const journeys = rows.reduce((acc, r) => acc + (r.journey_count || 0), 0);
      setTotals({
        journeys,
        uniqueVehicles: result.unique_vehicles_in_range || 0,
        activeDays: rows.length,
      });
    } catch (e: any) {
      setError(e.message || 'Failed to load data');
      setData([]);
      setTotals({ journeys: 0, uniqueVehicles: 0, activeDays: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!fetched.current) {
      fetched.current = true;
      load(dateFrom, dateTo);
    }
  }, [load, dateFrom, dateTo]);

  const validateRange = (from: string, to: string): string | null => {
    if (!from || !to) return 'Both From and To dates are required.';
    if (from > to) return 'From date must be on or before To date.';
    if (diffDays(from, to) > MAX_RANGE_DAYS) return 'Date range cannot exceed 3 months.';
    return null;
  };

  const handleApply = () => {
    const validationError = validateRange(dateFrom, dateTo);
    if (validationError) {
      setError(validationError);
      return;
    }
    load(dateFrom, dateTo);
  };

  const handleExportCsv = () => {
    const validationError = validateRange(dateFrom, dateTo);
    if (validationError) {
      setError(validationError);
      return;
    }
    const url = `${API_BASE}?date_from=${encodeURIComponent(dateFrom)}&date_to=${encodeURIComponent(dateTo)}&format=csv`;
    window.location.assign(url);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>
          NMAC Journeys
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Daily journey counts and unique vehicle counts (NMAC fleet)
        </Typography>
      </Stack>

      {/* Filter bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            label="From"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            sx={{ width: 170 }}
          />
          <TextField
            size="small"
            label="To"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            sx={{ width: 170 }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleApply}
            disabled={loading}
          >
            Apply
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            Max 3-month range
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportCsv}
            disabled={loading || !dateFrom || !dateTo}
          >
            Export CSV
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">Total Journeys</Typography>
            <Typography variant="h4" fontWeight={600}>{totals.journeys.toLocaleString()}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">Unique Vehicles</Typography>
            <Typography variant="h4" fontWeight={600}>{totals.uniqueVehicles.toLocaleString()}</Typography>
            <Typography variant="caption" color="text.secondary">
              Distinct VINs that drove in this range
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">Days With Activity</Typography>
            <Typography variant="h4" fontWeight={600}>{totals.activeDays.toLocaleString()}</Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* Chart */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Daily Activity
          </Typography>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={400}>
              <CircularProgress />
            </Box>
          ) : data.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={400}>
              <Typography color="text.secondary">No journey activity in this date range.</Typography>
            </Box>
          ) : (
            <Box sx={{ width: '100%', height: 460 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 10, right: 30, left: 10, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    angle={-45}
                    textAnchor="end"
                    interval="preserveStartEnd"
                    minTickGap={20}
                    tick={{ fontSize: 11 }}
                    tickMargin={10}
                    tickFormatter={(d: string) => d.slice(5)}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    labelFormatter={(label) => `Date: ${label}`}
                    formatter={(value: any) => Number(value).toLocaleString()}
                  />
                  <Legend wrapperStyle={{ paddingTop: 10 }} />
                  <Line
                    type="monotone"
                    dataKey="journey_count"
                    name="Journeys"
                    stroke="#2E77D0"
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    activeDot={{ r: 5 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="unique_vehicle_count"
                    name="Unique Vehicles"
                    stroke="#E85D75"
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default NmacJourneysPage;
